import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import { useCallback } from 'react';
import { PRELOAD_TIME_SECONDS, TRANSITION_THRESHOLD } from '../config/constants';
import { ClipStore } from '../types';

interface UseVideoTransitionLogicParams {
  store: ClipStore;
  currentClipIndex: number;
  activeVideo: 'video1' | 'video2';
  video1Ref: React.RefObject<HTMLVideoElement | null>;
  video2Ref: React.RefObject<HTMLVideoElement | null>;
  preloadingVideoRef: React.RefObject<'video1' | 'video2' | null>;
  video1ReadyRef: React.RefObject<boolean>;
  video2ReadyRef: React.RefObject<boolean>;
  setCurrentClipIndex: (index: number) => void;
  setActiveVideo: (video: 'video1' | 'video2') => void;
  setVideo1Clip: (clip: Clip | null) => void;
  setVideo2Clip: (clip: Clip | null) => void;
  setVideo1TargetTime: (time: number | undefined) => void;
  setVideo2TargetTime: (time: number | undefined) => void;
  lastVirtualTimestampRef: React.RefObject<number>;
}

export const useVideoTransitionLogic = ({
  store,
  currentClipIndex,
  activeVideo,
  video1Ref,
  video2Ref,
  preloadingVideoRef,
  video1ReadyRef,
  video2ReadyRef,
  setCurrentClipIndex,
  setActiveVideo,
  setVideo1Clip,
  setVideo2Clip,
  setVideo1TargetTime,
  setVideo2TargetTime,
  lastVirtualTimestampRef,
}: UseVideoTransitionLogicParams) => {
  const handleClipTransition = useCallback(
    (nextClipIndex: number) => {
      if (nextClipIndex < 0 || nextClipIndex >= store.clips.length) {
        store.setIsPlaying(false);
        store.setCurrentTime(store.totalClipsDuration);
        return;
      }

      const preloadingVideoElementRef =
        preloadingVideoRef.current === 'video1' ? video1Ref : video2Ref;
      const preloadingVideoElement = preloadingVideoElementRef.current;

      if (preloadingVideoElement) {
        const preloadingVideo = preloadingVideoRef.current;

        // Use requestAnimationFrame for immediate visual switch
        requestAnimationFrame(() => {
          setCurrentClipIndex(nextClipIndex);
          setActiveVideo(preloadingVideo!);
          preloadingVideoRef.current = null;

          // Start playing immediately in the next frame
          requestAnimationFrame(() => {
            if (store.isPlaying) {
              if (preloadingVideo === 'video2') {
                const video2 = video2Ref.current;
                if (video2) {
                  video2.play().catch(console.error);
                }
              } else {
                const video1 = video1Ref.current;
                if (video1) {
                  video1.play().catch(console.error);
                }
              }
            }
          });
        });
      }
    },
    [
      store.clips.length,
      store.totalClipsDuration,
      store.isPlaying,
      preloadingVideoRef,
      setCurrentClipIndex,
      setActiveVideo,
    ],
  );

  const handleTimeUpdate = useCallback(
    (videoRef: React.RefObject<HTMLVideoElement | null>, clipData: Clip | null) => {
      if (!clipData || !videoRef.current || !store.isPlaying) return;

      const video = videoRef.current;
      const isActiveVideo =
        (activeVideo === 'video1' && videoRef === video1Ref) ||
        (activeVideo === 'video2' && videoRef === video2Ref);

      if (!isActiveVideo) return;

      const isCurrentClipIndexValid =
        currentClipIndex >= 0 && currentClipIndex < store.clips.length;
      if (!isCurrentClipIndexValid) {
        store.setIsPlaying(false);
        return;
      }

      const timeUntilEnd = clipData.endTime - video.currentTime;
      const clipElapsedTime = Math.max(0, video.currentTime - clipData.startTime);
      const previousClipsDuration = store.clips
        .slice(0, currentClipIndex)
        .reduce((total: number, clip: Clip) => total + clip.duration, 0);
      const newVirtualTimestamp = previousClipsDuration + clipElapsedTime;

      const finalTimestamp = Math.min(
        store.totalClipsDuration,
        Math.max(lastVirtualTimestampRef.current, newVirtualTimestamp),
      );
      lastVirtualTimestampRef.current = finalTimestamp;
      store.setCurrentTime(finalTimestamp);

      // More aggressive preloading - start earlier
      if (timeUntilEnd <= PRELOAD_TIME_SECONDS && timeUntilEnd > 0) {
        if (!video.dataset.overlapTriggered) {
          video.dataset.overlapTriggered = 'true';

          if (currentClipIndex < store.clips.length - 1) {
            const nextClipIndex = currentClipIndex + 1;

            if (activeVideo === 'video1') {
              setVideo2Clip(store.clips[nextClipIndex]);
              setVideo2TargetTime(store.clips[nextClipIndex].startTime);
              video2ReadyRef.current = false;
              preloadingVideoRef.current = 'video2';
            } else {
              setVideo1Clip(store.clips[nextClipIndex]);
              setVideo1TargetTime(store.clips[nextClipIndex].startTime);
              video1ReadyRef.current = false;
              preloadingVideoRef.current = 'video1';
            }
          }
        }
      }

      // More aggressive transition - switch earlier to prevent overlap
      if (video.currentTime >= clipData.endTime - TRANSITION_THRESHOLD) {
        // Use requestAnimationFrame for smooth transition
        requestAnimationFrame(() => {
          if (video.currentTime >= clipData.endTime - TRANSITION_THRESHOLD) {
            video.pause();
            delete video.dataset.overlapTriggered;

            if (currentClipIndex >= store.clips.length - 1) {
              store.setIsPlaying(false);
              store.setCurrentTime(store.totalClipsDuration);
              return;
            }

            if (preloadingVideoRef.current && currentClipIndex < store.clips.length - 1) {
              const nextClipIndex = currentClipIndex + 1;
              handleClipTransition(nextClipIndex);
            }
          }
        });
      }
    },
    [
      store.isPlaying,
      store.clips,
      store.totalClipsDuration,
      activeVideo,
      currentClipIndex,
      video1Ref,
      video2Ref,
      lastVirtualTimestampRef,
      preloadingVideoRef,
      setVideo1Clip,
      setVideo2Clip,
      setVideo1TargetTime,
      setVideo2TargetTime,
      handleClipTransition,
    ],
  );

  return {
    handleClipTransition,
    handleTimeUpdate,
  };
};
